import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Users,
  ShoppingCart,
  Settings,
  LogOut,
  FolderTree,
  Layers,
  Menu,
  X,
  ChevronDown,
  Image,
  Home,
  FileText,
  Bell
} from 'lucide-react';
import { supabase } from '../lib/supabase';

interface AdminPermissions {
  orders: boolean;
  products: boolean;
  catalogs: boolean;
  users: boolean;
  reports: boolean;
}

const TABS = [
  { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard, permission: 'reports' },
  { id: 'products', name: 'Productos', icon: Package, permission: 'products' },
  { id: 'categories', name: 'Categorías', icon: FolderTree, permission: 'products' },
  { id: 'collections', name: 'Colecciones', icon: Layers, permission: 'products' },
  { id: 'catalogs', name: 'Catálogos', icon: FileText, permission: 'catalogs' },
  { id: 'sliders', name: 'Sliders', icon: Image, permission: 'products' },
  { id: 'users', name: 'Usuarios', icon: Users, permission: 'users' },
  { id: 'orders', name: 'Pedidos', icon: ShoppingCart, permission: 'orders' },
  { id: 'notifications', name: 'Notificaciones', icon: Bell, permission: 'orders' },
  { id: 'settings', name: 'Configuración', icon: Settings, permission: undefined },
] as const;

interface AdminLayoutProps {
  children: React.ReactNode;
  activeTab: (typeof TABS)[number]['id'];
  onTabChange: (tab: (typeof TABS)[number]['id']) => void;
}

export default function AdminLayout({ children, activeTab, onTabChange }: AdminLayoutProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [permissions, setPermissions] = useState<AdminPermissions | null>(null);
  const [adminProfile, setAdminProfile] = useState<any>(null);

  // Verificar si el usuario tiene permiso para acceder a una pestaña específica
  const hasTabPermission = (tabId: string): boolean => {
    if (isAdmin) return true;

    const tab = TABS.find(t => t.id === tabId);
    if (!tab) return false;
    if (!tab.permission) return true;

    return Boolean(permissions?.[tab.permission as keyof AdminPermissions]);
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError) throw authError;
        if (!user) {
          navigate('/login');
          return;
        }

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profileError) throw profileError;

        const userPermissions = profile?.admin_permissions || {};
        const isFullAdmin = Object.values(userPermissions).every(Boolean);
        const isHardAdmin = Boolean(profile?.is_admin);

        setIsAdmin(isFullAdmin || isHardAdmin);
        setPermissions(userPermissions);
        setAdminProfile({
          ...profile,
          email: user.email
        });

        // Verificar si el usuario tiene permiso para la pestaña actual
        if (!hasTabPermission(activeTab)) {
          const firstAllowedTab = TABS.find(tab => {
            if (!tab.permission) return true;
            return Boolean(userPermissions[tab.permission as keyof AdminPermissions]);
          });

          if (firstAllowedTab) {
            onTabChange(firstAllowedTab.id);
          } else {
            navigate('/access-denied');
          }
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate, activeTab]);

  // Cerrar sidebar en mobile al cambiar de tamaño
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTabChange = (tabId: (typeof TABS)[number]['id']) => {
    if (hasTabPermission(tabId)) {
      onTabChange(tabId);
      // Cerrar sidebar en móvil después de seleccionar
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      }
    } else {
      alert('No tienes permisos para acceder a esta sección');
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Cargando panel de administración...</p>
        </div>
      </div>
    );
  }

  const accessibleTabs = TABS.filter(tab => hasTabPermission(tab.id));

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Overlay para móvil cuando el sidebar está abierto */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full w-64 bg-white border-r z-50 transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col
        `}
      >
        {/* Logo y toggle */}
        <div className="h-16 border-b flex items-center justify-between px-4">
          <a href="/" className="flex items-center">
            <img src="/assets/DISOLGUA.png" alt="Disolgua Logo" className="h-8 w-auto" />
          </a>
          <button
            onClick={toggleSidebar}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Información del usuario (solo móvil) */}
        <div className="lg:hidden px-4 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <span className="text-blue-600 font-medium text-sm">
                {adminProfile?.name.charAt(0)}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">{adminProfile?.name}</p>
              <p className="text-xs text-gray-500 truncate">{adminProfile?.email}</p>
            </div>
          </div>
        </div>

        {/* Navegación */}
        <nav className="flex-1 overflow-y-auto p-3">
          <ul className="space-y-1">
            {TABS.map((tab) => {
              if (!hasTabPermission(tab.id)) return null;

              return (
                <li key={tab.id}>
                  <button
                    onClick={() => handleTabChange(tab.id)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                      ${activeTab === tab.id
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <tab.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="truncate">{tab.name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Botones inferiores */}
        <div className="p-3 border-t space-y-2">
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-medium transition-colors"
          >
            <Home className="w-5 h-5 flex-shrink-0" />
            <span className="truncate">Ir a Tienda</span>
          </button>

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span className="truncate">Cerrar sesión</span>
          </button>
        </div>
      </aside>

      {/* Contenido principal */}
      <div className="lg:ml-64 min-h-screen flex flex-col">
        {!hasTabPermission(activeTab) ? (
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Acceso Denegado</h2>
              <p className="text-gray-600">No tienes permisos para acceder a esta sección.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <header className="sticky top-0 z-30 bg-white border-b">
              <div className="h-16 px-4 flex items-center justify-between gap-4">
                {/* Lado izquierdo */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <button
                    onClick={toggleSidebar}
                    className="lg:hidden p-2 hover:bg-gray-100 rounded-lg text-gray-600"
                  >
                    <Menu className="w-5 h-5" />
                  </button>

                  <h1 className="text-lg font-semibold text-gray-900 truncate">
                    {TABS.find(tab => tab.id === activeTab)?.name}
                  </h1>
                </div>

                {/* Lado derecho */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Botón Ir a Tienda (desktop) */}
                  <button
                    onClick={() => navigate('/')}
                    className="hidden md:flex items-center gap-2 text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    <Home className="w-4 h-4" />
                    <span>Tienda</span>
                  </button>

                  {/* Menú de usuario */}
                  <div className="relative">
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className="flex items-center gap-2 hover:bg-gray-50 p-2 rounded-lg transition-colors"
                    >
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-medium text-sm">
                          {adminProfile?.name.charAt(0)}
                        </span>
                      </div>
                      <div className="hidden md:block text-left min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate max-w-[150px]">
                          {adminProfile?.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate max-w-[150px]">
                          {adminProfile?.email}
                        </p>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-500 hidden md:block transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {userMenuOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setUserMenuOpen(false)}
                        />
                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border z-50 overflow-hidden">
                          <div className="px-4 py-3 border-b bg-gray-50">
                            <p className="text-sm font-medium text-gray-900 truncate">{adminProfile?.name}</p>
                            <p className="text-xs text-gray-500 truncate">{adminProfile?.email}</p>
                          </div>
                          <div className="py-1">
                            <button
                              onClick={handleLogout}
                              className="w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 text-left flex items-center gap-2 transition-colors"
                            >
                              <LogOut className="w-4 h-4" />
                              <span>Cerrar sesión</span>
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </header>

            {/* Contenido de la página */}
            <main className="flex-1 p-4 md:p-6 pb-20 lg:pb-6 overflow-x-hidden">
              <div className="max-w-[1600px] mx-auto w-full">
                {children}
              </div>
            </main>

            {/* Barra de navegación inferior para móvil */}
            <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t z-30 safe-area-bottom">
              <div className="flex items-center justify-around h-16 px-2">
                {accessibleTabs.slice(0, 4).map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`
                      flex flex-col items-center justify-center gap-1 px-2 py-1 rounded-lg flex-1 transition-colors
                      ${activeTab === tab.id
                        ? 'text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                      }
                    `}
                  >
                    <tab.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="text-[10px] font-medium truncate max-w-[60px]">
                      {tab.name}
                    </span>
                  </button>
                ))}

                <button
                  onClick={toggleSidebar}
                  className="flex flex-col items-center justify-center gap-1 px-2 py-1 rounded-lg flex-1 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <Menu className="w-5 h-5 flex-shrink-0" />
                  <span className="text-[10px] font-medium">Más</span>
                </button>
              </div>
            </nav>
          </>
        )}
      </div>
    </div>
  );
}
